/**
 * TaxonomyDetailView
 *
 * Detail view for a single taxonomy key. Contains all the original
 * TaxonomyPage functionality: dashboard cards, 2-column layout with
 * version content and sidebar, view mode toggle, JSON editing, and modals.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  isStructuredValidationError,
  type StructuredValidationDetail,
} from '../../../../../services/types/errors';
import { AlignJustify, Image } from 'lucide-react';
import { Button } from '../../../../ui/button';
import { Textarea } from '../../../../ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../ui/tooltip';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '../../../../ui/breadcrumb';
import {
  useDashboard,
  useVersionList,
  useVersionDetail,
  useUpdateDraft,
} from '../../hooks/useTaxonomy';
import VersionBadge from '../../shared/VersionBadge';
import TaxonomyValidationErrorModal from './TaxonomyValidationErrorModal';
import VersionTimelineSidebar from './VersionTimelineSidebar';
import { useComputeImpact } from '../../annotate/hooks/useImpact';
import ReannotationImpactModal from '../../annotate/components/snapshots/ReannotationImpactModal';
import type { ImpactResult } from '../../annotate/types';

interface TaxonomyDetailViewProps {
  taxonomyKey: string;
  displayName: string;
  onBack: () => void;
}

const TaxonomyDetailView: React.FC<TaxonomyDetailViewProps> = ({
  taxonomyKey,
  displayName,
  onBack,
}) => {
  const navigate = useNavigate();
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [editJson, setEditJson] = useState('');
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [viewMode, setViewMode] = useState<'formatted' | 'raw'>('raw');

  const { data: dashboard, isLoading: dashboardLoading, isError: dashboardError, refetch: refetchDashboard } = useDashboard(taxonomyKey);
  const { data: versions, isLoading: versionsLoading } = useVersionList(taxonomyKey);
  const { data: selectedVersion } = useVersionDetail(taxonomyKey, selectedVersionId || '');
  const updateDraft = useUpdateDraft();
  const computeImpact = useComputeImpact();
  const [impactSummary, setImpactSummary] = useState<ImpactResult | null>(null);
  const [validationDetail, setValidationDetail] = useState<StructuredValidationDetail | null>(null);

  // A save belongs to the version it was started on. The timeline stays clickable
  // while one is in flight, so without this a rejected save would open its problem
  // list over a different version's JSON — paths the author cannot act on.
  const selectedVersionIdRef = useRef<string | null>(null);
  selectedVersionIdRef.current = selectedVersionId;

  // Auto-select the first version (newest) on initial load
  useEffect(() => {
    if (!selectedVersionId && versions && versions.length > 0) {
      setSelectedVersionId(versions[0].id);
    }
  }, [versions, selectedVersionId]);

  // Populate editor when selected version changes
  useEffect(() => {
    if (selectedVersion?.taxonomyData) {
      setEditJson(JSON.stringify(selectedVersion.taxonomyData, null, 2));
      setHasUnsaved(false);
    }
  }, [selectedVersion?.id, selectedVersion?.contentHash, selectedVersion?.taxonomyData]);

  const handleSaveDraft = () => {
    if (!selectedVersionId || !editJson.trim()) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(editJson);
    } catch {
      toast.error('Invalid JSON — please check your syntax');
      return;
    }
    // Snapshot the BEFORE blob NOW — once `updateDraft.mutate` runs, its
    // `onSuccess` invalidates the version-detail cache and `selectedVersion`
    // will refetch to the new payload, so we'd lose the original otherwise.
    const previousTaxonomyData = selectedVersion?.taxonomyData;
    const savingVersionId = selectedVersionId;
    updateDraft.mutate(
      { taxonomyKey, versionId: selectedVersionId, taxonomyData: parsed as Parameters<typeof updateDraft.mutate>[0]['taxonomyData'] },
      {
        onError: (error) => {
          // The hook-level handler stays silent for these so the modal, which can
          // show every problem and how to fix it, is the only surface.
          if (selectedVersionIdRef.current !== savingVersionId) return;
          if (isStructuredValidationError(error)) {
            setValidationDetail(error.error.detail);
          }
        },
        onSuccess: async () => {
          setHasUnsaved(false);
          if (!previousTaxonomyData) return;
          // Re-annotation impact is a non-blocking secondary action: if it
          // fails, the save itself already succeeded, so we swallow the
          // error rather than surfacing a follow-up toast that would muddy
          // the editor's success state.
          try {
            const impact = await computeImpact.mutateAsync({
              taxonomyKey,
              previousTaxonomyData,
              newTaxonomyData: parsed,
            });
            // Suppress the modal on no-op edits (e.g. whitespace-only or
            // description-only changes that don't touch any dimension).
            if (
              impact.changedDimensions.length > 0 ||
              impact.recordsAffected > 0 ||
              impact.finalisedRecordsToReview > 0
            ) {
              setImpactSummary(impact);
            }
          } catch {
            // Best-effort: swallow silently. The save already succeeded
            // and the impact modal is a secondary, non-blocking surface.
          }
        },
      }
    );
  };

  const closeImpactModal = () => setImpactSummary(null);
  const onImpactGoToQueue = () => {
    setImpactSummary(null);
    navigate('/v4/go-lab/annotate/queue');
  };

  const isDraft = selectedVersion?.status === 'draft';

  if (dashboardLoading || versionsLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="h-8 w-48 bg-gray-200 animate-pulse rounded" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-100 animate-pulse rounded-lg" />
          ))}
        </div>
        <div className="h-96 bg-gray-100 animate-pulse rounded-lg" />
      </div>
    );
  }

  if (dashboardError) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-[50vh] text-center">
        <p className="text-gray-500 mb-4">Failed to load taxonomy data.</p>
        <Button variant="outline" onClick={() => refetchDashboard()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-6 rounded-2xl border border-border/70 bg-card p-6 shadow-[var(--shadow-card)]">
      {/* Breadcrumb Navigation */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink className="cursor-pointer" onClick={onBack}>
              Taxonomy
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{displayName}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Page Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{displayName}</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage taxonomy versions
          </p>
        </div>
        <Button
          variant="toolbar"
          size="sm"
          onClick={() => navigate(`/v4/go-lab/intelligence/prompts?taxonomyKey=${taxonomyKey}`)}
        >
          View Prompts
        </Button>
      </div>

      {/* Dashboard Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg border bg-white p-4">
          <p className="text-sm text-gray-500">Live Version</p>
          <p className="text-2xl font-bold">
            {dashboard?.liveTaxonomy ? `v${dashboard.liveTaxonomy.versionNumber}` : 'None'}
          </p>
          {dashboard?.liveTaxonomy && (
            <p className="text-xs text-gray-400 mt-1">
              {dashboard.liveTaxonomy.labelCount} labels, {dashboard.liveTaxonomy.dimensionCount} dimensions
            </p>
          )}
        </div>
        <div className="rounded-lg border bg-white p-4">
          <p className="text-sm text-gray-500">Total Versions</p>
          <p className="text-2xl font-bold">{dashboard?.totalVersions ?? 0}</p>
        </div>
      </div>

      {/* Main Content: 2-column layout */}
      <div className="flex gap-6 flex-1 min-h-0 overflow-hidden">
        {/* Left: Selected version content */}
        <div className="flex-1 min-w-0 min-h-0">
          <div className="rounded-lg border bg-white p-6 h-full flex flex-col overflow-hidden">
            {/* Version header */}
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-lg font-semibold">
                {selectedVersion ? `v${selectedVersion.versionNumber}` : 'No version selected'}
              </h2>
              {selectedVersion && <VersionBadge status={selectedVersion.status} />}
            </div>

            {/* Controls row — Save + view toggle icons */}
            {selectedVersion && (
              <div className="flex items-baseline justify-between mb-4">
                <p className="text-xs text-gray-500">
                  {isDraft && viewMode === 'raw'
                    ? 'Edit the taxonomy JSON below. Save when ready.'
                    : '\u00A0'}
                </p>
                <div className="flex items-center gap-2">
                  {isDraft && (
                    <Button
                      size="sm"
                      onClick={handleSaveDraft}
                      disabled={!hasUnsaved || updateDraft.isPending}
                    >
                      {updateDraft.isPending ? 'Saving...' : hasUnsaved ? 'Save' : 'Saved'}
                    </Button>
                  )}
                  <TooltipProvider delayDuration={300}>
                    <div className="flex gap-0.5">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant={viewMode === 'raw' ? 'default' : 'ghost'}
                            className="h-7 w-7"
                            aria-label="Raw JSON view"
                            onClick={() => setViewMode('raw')}
                          >
                            <AlignJustify className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Raw JSON</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant={viewMode === 'formatted' ? 'default' : 'ghost'}
                            className="h-7 w-7"
                            aria-label="Formatted view"
                            onClick={() => setViewMode('formatted')}
                          >
                            <Image className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Formatted</TooltipContent>
                      </Tooltip>
                    </div>
                  </TooltipProvider>
                </div>
              </div>
            )}

            {/* Version content */}
            {selectedVersion ? (
              viewMode === 'raw' ? (
                isDraft ? (
                  <Textarea
                    value={editJson}
                    onChange={(e) => { setEditJson(e.target.value); setHasUnsaved(true); }}
                    className="flex-1 min-h-0 font-mono text-sm resize-none overflow-auto"
                    spellCheck={false}
                  />
                ) : (
                  <pre className="flex-1 min-h-0 whitespace-pre-wrap font-mono text-sm text-gray-700 leading-relaxed overflow-auto">
                    {JSON.stringify(selectedVersion.taxonomyData, null, 2)}
                  </pre>
                )
              ) : (
                <div className="space-y-3 flex-1 min-h-0 overflow-auto">
                  {selectedVersion.taxonomyData.dimensions.map((dim) => (
                    <div key={dim.id} className="border rounded-md p-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-sm">{dim.name}</h3>
                        <span className="text-xs text-gray-400">{dim.id}</span>
                      </div>
                      {dim.description && (
                        <p className="text-xs text-gray-500 mt-1">{dim.description}</p>
                      )}
                      <TooltipProvider delayDuration={200}>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {dim.categories.map((cat) => (
                            <Tooltip key={cat.id}>
                              <TooltipTrigger asChild>
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded cursor-default">
                                  {cat.name} ({cat.labels.length})
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" sideOffset={4} showArrow={false} className="max-w-xs bg-white text-black border shadow-md">
                                <p className="font-medium text-xs mb-1">{cat.name}</p>
                                <ul className="text-xs space-y-0.5">
                                  {cat.labels.map((label) => (
                                    <li key={label.id}>&#8226; {label.name}</li>
                                  ))}
                                </ul>
                              </TooltipContent>
                            </Tooltip>
                          ))}
                        </div>
                      </TooltipProvider>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <p className="text-gray-500">Select a version from the sidebar.</p>
            )}
          </div>

        </div>

        {/* Right: Version sidebar */}
        <VersionTimelineSidebar
          taxonomyKey={taxonomyKey}
          liveVersionId={versions?.find((v) => v.status === 'live')?.id}
          selectedVersionId={selectedVersionId}
          onSelectVersion={setSelectedVersionId}
          classifiedSignalCount={dashboard?.signalVersionStats?.totalClassified}
        />
      </div>

      {/* Create New Taxonomy Modal */}

      {validationDetail && (
        <TaxonomyValidationErrorModal
          open
          onOpenChange={(o) => {
            if (!o) setValidationDetail(null);
          }}
          detail={validationDetail}
        />
      )}

      {impactSummary && (
        <ReannotationImpactModal
          open
          onOpenChange={(o) => {
            if (!o) closeImpactModal();
          }}
          summary={impactSummary}
          onContinueEditing={closeImpactModal}
          onGoToQueue={onImpactGoToQueue}
        />
      )}
    </div>
  );
};

export default TaxonomyDetailView;
